// Client for /api/form-templates. Definitions live on the server
// (server/services/formCatalog.js) and are fetched, not duplicated here, so
// an edited label or hidden field takes effect everywhere at once. One
// in-flight request is shared by every caller; saving or resetting a form
// calls invalidateForms() so the next read is fresh.

import { useEffect, useState } from "react";
import api from "./api";

let cache = null;

export function loadForms() {
  if (!cache) {
    cache = api.get("/api/form-templates")
      .then((d) => d.forms || [])
      .catch((e) => { cache = null; throw e; });
  }
  return cache;
}

export function invalidateForms() { cache = null; }

// One form's definition, by key. Returns { def, loading, error, reload }.
export function useFormDefinition(key) {
  const [state, setState] = useState({ def: null, loading: true, error: null });

  const fetchIt = (fresh) => {
    if (fresh) invalidateForms();
    setState((s) => ({ ...s, loading: true, error: null }));
    loadForms()
      .then((forms) => setState({ def: forms.find((f) => f.key === key) || null, loading: false, error: null }))
      .catch((e) => setState({ def: null, loading: false, error: e.message || "Couldn't load the form layout" }));
  };

  useEffect(() => { fetchIt(false); }, [key]); // eslint-disable-line
  return { ...state, reload: () => fetchIt(true) };
}

// Every form, for the catalogue.
export function useFormCatalogue() {
  const [state, setState] = useState({ forms: [], loading: true, error: null });
  const fetchIt = (fresh) => {
    if (fresh) invalidateForms();
    setState((s) => ({ ...s, loading: true, error: null }));
    loadForms()
      .then((forms) => setState({ forms, loading: false, error: null }))
      .catch((e) => setState({ forms: [], loading: false, error: e.message || "Couldn't load the forms" }));
  };
  useEffect(() => { fetchIt(false); }, []); // eslint-disable-line
  return { ...state, reload: () => fetchIt(true) };
}

// ─── Reading a definition ─────────────────────────────────────────────────
export const sectionOf = (def, id) => (def?.sections || []).find((s) => s.id === id) || null;

// Fields of one section in HR's order, hidden ones dropped unless asked.
export const fieldsOf = (def, sectionId, { includeHidden = false } = {}) =>
  (sectionOf(def, sectionId)?.fields || []).filter((f) => includeHidden || !f.hidden);

export const fieldOf = (def, sectionId, fieldId) =>
  (sectionOf(def, sectionId)?.fields || []).find((f) => f.id === fieldId) || null;

// A visible field's label, or null when hidden — live forms use this to
// decide whether to render a control at all.
export const labelOf = (def, sectionId, fieldId) => {
  const f = fieldOf(def, sectionId, fieldId);
  return f && !f.hidden ? f.label : null;
};

export const ratingsOf = (def, sectionId) =>
  fieldsOf(def, sectionId).filter((f) => f.type === "rating");

// Required check for a section's visible fields; the server re-checks.
export const missingRequired = (def, sectionId, values) =>
  fieldsOf(def, sectionId).find((f) => f.required && f.type !== "read"
    && !String(values?.[f.id] ?? "").trim()) || null;
